import type { ReactNode } from 'react';
import {
    CpmBadge,
    CpmButton,
    CpmChip,
    CpmEmptyState,
    CpmErrorCard,
    CpmFilterDropdown,
    CpmIconButton,
    CpmInlineErrorBanner,
    CpmTooltip,
} from '../../../components/design-system';
import type { ConfigTemplateLevel } from '../../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    listCountLine,
    newTemplateLabel,
    placeFailRetry,
    placeFailTitle,
    rowSettingsLine,
    rowStatusFailedLabel,
    trueEmptyTitle,
} from '../configTemplateCopy';
import { useTemplatesList } from '../hooks/useTemplatesList';
import { licenceKey, licenceLabel, parseLicenceKey } from '../utils/licenceLabels';
import { catalogStateName, toStateFilterOptions } from '../utils/stateCatalog';
import ChipOverflow from './ChipOverflow';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';
import TemplatesListSkeleton from './TemplatesListSkeleton';

interface TemplatesListProps {
    level: ConfigTemplateLevel;
}

const TABLE_COLS = 'minmax(0,2.2fr) minmax(0,1.4fr) minmax(0,1.2fr) minmax(0,1.1fr) minmax(72px,0.85fr) 300px';

function PencilIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
    );
}

function DuplicateIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

function RowGhostButton({
    icon,
    children,
    disabled,
    onClick,
}: {
    icon?: ReactNode;
    children: ReactNode;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className="inline-flex items-center justify-center shrink-0 hover:text-[var(--cpm-brand-navy)] hover:bg-[var(--cpm-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
            style={{
                height: 32,
                padding: '0 10px',
                gap: 8,
                border: 'none',
                background: 'transparent',
                borderRadius: 'var(--cpm-radius-10)',
                color: 'var(--cpm-text-secondary)',
                fontFamily: 'var(--cpm-font-sans)',
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
        >
            {icon}
            {children}
        </button>
    );
}

function SearchIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-text-tertiary)"
            strokeWidth="2"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.35-4.35" strokeLinecap="round" />
        </svg>
    );
}

function InfoIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

function PlusIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        >
            <path d="M12 5v14M5 12h14" />
        </svg>
    );
}

export default function TemplatesList({ level }: TemplatesListProps) {
    const list = useTemplatesList(level);
    const { place } = list;
    const placeBusy = place.status === 'loading' || place.status === 'idle';
    const newDisabled = placeBusy || place.status === 'error';
    const isBrand = level === 'STORE_BRAND';

    const catalogStates = Array.isArray(list.states.states) ? list.states.states : [];
    const stateOptions = toStateFilterOptions(catalogStates);
    const licenceOptions = catalogStates.flatMap((state) => {
        const stateName = catalogStateName(state);
        if (!stateName) return [];
        return (state.licenseTypes ?? []).map((type) => ({
            value: licenceKey({ state: stateName, code: type.code }),
            label: `${stateName} · ${type.label}`,
        }));
    });

    const appliedChips: { key: string; label: string; onClear: () => void }[] = [];
    for (const state of place.filters.states) {
        appliedChips.push({
            key: `state:${state}`,
            label: state,
            onClear: () =>
                list.setStateFilter(place.filters.states.filter((item) => item !== state)),
        });
    }
    for (const tag of place.filters.tags) {
        appliedChips.push({
            key: `tag:${tag}`,
            label: tag,
            onClear: () => list.setTagFilter(place.filters.tags.filter((item) => item !== tag)),
        });
    }
    for (const status of place.filters.activeStatus) {
        appliedChips.push({
            key: `status:${status}`,
            label: status,
            onClear: () => list.toggleActiveFilter(status),
        });
    }
    for (const licence of place.filters.licenceTypes) {
        appliedChips.push({
            key: `licence:${licenceKey(licence)}`,
            label: licenceLabel(licence, catalogStates),
            onClear: () =>
                list.setLicenceFilter(
                    place.filters.licenceTypes.filter(
                        (item) => licenceKey(item) !== licenceKey(licence)
                    )
                ),
        });
    }

    const showTrueEmpty =
        place.status === 'success' &&
        place.total === 0 &&
        !list.hasActiveFilters &&
        !list.waitingSearch;
    const showFilteredEmpty =
        place.status === 'success' &&
        place.templates.length === 0 &&
        list.hasActiveFilters &&
        !list.waitingSearch;

    return (
        <>
            <div
                className="flex flex-wrap items-center"
                style={{ gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--cpm-border-default)' }}
            >
                <div style={{ position: 'relative', width: 300 }}>
                    <span
                        className="absolute inset-y-0 left-0 flex items-center pointer-events-none"
                        style={{ paddingLeft: 13 }}
                    >
                        <SearchIcon />
                    </span>
                    <input
                        type="search"
                        value={list.searchInput}
                        onChange={(event) => list.onSearchChange(event.target.value)}
                        placeholder={CONFIG_TEMPLATE_COPY.SEARCH_PLACEHOLDER}
                        className="h-[38px] w-full rounded-[var(--cpm-radius-10)] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[13.5px] font-semibold text-[var(--cpm-text-primary)] outline-none focus:border-[var(--cpm-brand-purple)] focus:shadow-[var(--cpm-ring-focus)]"
                        style={{ paddingLeft: 34, paddingRight: 13 }}
                    />
                </div>

                <CpmFilterDropdown
                    label={CONFIG_TEMPLATE_COPY.STATES}
                    triggerVariant="chip"
                    options={stateOptions}
                    selected={place.filters.states}
                    onChange={list.setStateFilter}
                    multi
                    emptyLabel={CONFIG_TEMPLATE_COPY.NONE_SELECTED}
                    portal
                    loading={list.states.status === 'loading' || list.states.status === 'idle'}
                    loadingLabel={CONFIG_TEMPLATE_COPY.COPY_STATE_OPTIONS_WAIT}
                    failed={list.states.status === 'error'}
                    failedTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_STATES}
                    failedBody={CONFIG_TEMPLATE_COPY.OPTIONS_FAIL_BODY}
                    onRetry={list.retryStates}
                />
                <CpmFilterDropdown
                    label={CONFIG_TEMPLATE_COPY.TAGS}
                    triggerVariant="chip"
                    options={list.tags.tags.map((tag) => ({ value: tag, label: tag }))}
                    selected={place.filters.tags}
                    onChange={list.setTagFilter}
                    multi
                    emptyLabel={CONFIG_TEMPLATE_COPY.NONE_SELECTED}
                    portal
                    loading={list.tags.status === 'loading' || list.tags.status === 'idle'}
                    loadingLabel={CONFIG_TEMPLATE_COPY.LOADING_TAGS}
                    failed={list.tags.status === 'error'}
                    failedTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_TAGS}
                    failedBody={CONFIG_TEMPLATE_COPY.OPTIONS_FAIL_BODY}
                    onRetry={list.retryTags}
                />
                <CpmFilterDropdown
                    label={CONFIG_TEMPLATE_COPY.LICENCE_TYPE}
                    triggerVariant="chip"
                    options={licenceOptions}
                    selected={place.filters.licenceTypes.map((item) => licenceKey(item))}
                    onChange={(next) => {
                        const parsed = next
                            .map((key) => parseLicenceKey(key))
                            .filter((item): item is { state: string; code: string } => item != null);
                        list.setLicenceFilter(parsed);
                    }}
                    multi
                    emptyLabel={CONFIG_TEMPLATE_COPY.NONE_SELECTED}
                    portal
                    loading={list.states.status === 'loading' || list.states.status === 'idle'}
                    loadingLabel={CONFIG_TEMPLATE_COPY.COPY_STATE_OPTIONS_WAIT}
                    failed={list.states.status === 'error'}
                    failedTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_LICENCES}
                    failedBody={CONFIG_TEMPLATE_COPY.OPTIONS_FAIL_BODY}
                    onRetry={list.retryStates}
                />

                <div
                    className="flex items-center"
                    style={{
                        gap: 7,
                        paddingLeft: 10,
                        borderLeft: '1px solid var(--cpm-border-default)',
                        marginLeft: 2,
                    }}
                >
                    <span
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.STATUS}
                    </span>
                    <CpmChip
                        active={place.filters.activeStatus.includes('Active')}
                        onClick={() => list.toggleActiveFilter('Active')}
                    >
                        {CONFIG_TEMPLATE_COPY.ACTIVE}
                    </CpmChip>
                    <CpmChip
                        active={place.filters.activeStatus.includes('Inactive')}
                        onClick={() => list.toggleActiveFilter('Inactive')}
                    >
                        {CONFIG_TEMPLATE_COPY.INACTIVE}
                    </CpmChip>
                </div>

                <div style={{ flex: '1 1 auto' }} />

                <div className="flex items-center shrink-0" style={{ gap: 8 }}>
                    <span
                        style={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: 'var(--cpm-text-secondary)',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {listCountLine(
                            place.templates.length,
                            place.total,
                            level,
                            list.hasActiveFilters
                        )}
                    </span>
                    <CpmTooltip
                        content={
                            isBrand
                                ? CONFIG_TEMPLATE_COPY.COUNT_TOOLTIP_BRAND
                                : CONFIG_TEMPLATE_COPY.COUNT_TOOLTIP_STORE
                        }
                        align="center"
                        fit
                    >
                        <span
                            className="inline-flex items-center"
                            style={{ color: 'var(--cpm-text-tertiary)' }}
                        >
                            <InfoIcon />
                        </span>
                    </CpmTooltip>
                </div>

                <CpmButton
                    variant="navy"
                    disabled={newDisabled}
                    onClick={list.openNew}
                    leadingIcon={<PlusIcon />}
                    className="shrink-0"
                    style={{
                        height: 48,
                        paddingLeft: 20,
                        paddingRight: 20,
                        fontSize: 14,
                        background: 'var(--cpm-gradient-hero)',
                    }}
                >
                    {newTemplateLabel(level)}
                </CpmButton>
            </div>

            {appliedChips.length > 0 ? (
                <div
                    className="flex flex-wrap items-center"
                    style={{
                        gap: 8,
                        padding: '11px 20px',
                        borderBottom: '1px solid var(--cpm-border-default)',
                        background: 'var(--cpm-purple-tint-3)',
                    }}
                >
                    <span
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-brand-purple)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.FILTERS}
                    </span>
                    {appliedChips.map((chip) => (
                        <CpmChip key={chip.key} variant="token" onRemove={chip.onClear}>
                            {chip.label}
                        </CpmChip>
                    ))}
                    <button
                        type="button"
                        onClick={list.clearFilters}
                        className="border-0 bg-transparent cursor-pointer"
                        style={{ padding: '2px 4px', fontSize: 12, fontWeight: 800, color: 'var(--cpm-brand-navy)' }}
                    >
                        {CONFIG_TEMPLATE_COPY.CLEAR_ALL}
                    </button>
                </div>
            ) : null}

            {place.persistError ? (
                <div style={{ padding: '14px 20px 0' }}>
                    <CpmInlineErrorBanner
                        compact
                        message={CONFIG_TEMPLATE_COPY.PLACE_PERSIST_FAIL}
                        onRetry={list.refresh}
                        retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                    />
                </div>
            ) : null}

            {placeBusy && place.templates.length === 0 ? (
                <div style={{ padding: 20 }}>
                    <TemplatesListSkeleton />
                </div>
            ) : null}

            {place.status === 'error' ? (
                <div style={{ padding: 20 }}>
                    <CpmErrorCard
                        title={placeFailTitle(level)}
                        body={CONFIG_TEMPLATE_COPY.PLACE_FAIL_BODY}
                        onRetry={list.refresh}
                        retryLabel={placeFailRetry(level)}
                    />
                </div>
            ) : null}

            {showTrueEmpty ? (
                <CpmEmptyState
                    title={trueEmptyTitle(level)}
                    action={
                        <CpmButton
                            variant="navy"
                            leadingIcon={<PlusIcon />}
                            onClick={list.openNew}
                            style={{
                                height: 48,
                                paddingLeft: 20,
                                paddingRight: 20,
                                fontSize: 14,
                                background: 'var(--cpm-gradient-hero)',
                            }}
                        >
                            {newTemplateLabel(level)}
                        </CpmButton>
                    }
                />
            ) : null}

            {showFilteredEmpty ? (
                <CpmEmptyState
                    title={CONFIG_TEMPLATE_COPY.FILTERED_EMPTY}
                    action={
                        <CpmButton variant="outline" size="md" onClick={list.clearFilters}>
                            {CONFIG_TEMPLATE_COPY.CLEAR_FILTERS}
                        </CpmButton>
                    }
                />
            ) : null}

            {place.status === 'success' && place.templates.length > 0 ? (
                <>
                    <div className="overflow-x-auto">
                        <div
                            className="grid items-center"
                            style={{
                                gridTemplateColumns: TABLE_COLS,
                                minWidth: 980,
                                background: 'var(--cpm-purple-tint-13)',
                                borderBottom: '2px solid var(--cpm-brand-purple)',
                                boxShadow: '0 2px 10px -6px rgba(14,8,88,0.35)',
                                fontSize: 11.5,
                                fontWeight: 800,
                                letterSpacing: '0.07em',
                                textTransform: 'uppercase',
                                color: 'var(--cpm-brand-navy)',
                            }}
                        >
                            <button
                                type="button"
                                onClick={list.toggleNameSort}
                                className="flex items-center bg-transparent border-0 cursor-pointer"
                                style={{
                                    gap: 6,
                                    padding: '11px 16px',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.07em',
                                    textTransform: 'uppercase',
                                    color: 'var(--cpm-brand-navy)',
                                }}
                            >
                                <span>{CONFIG_TEMPLATE_COPY.NAME}</span>
                                <span style={{ fontSize: 11, color: 'var(--cpm-brand-purple)' }}>
                                    {place.filters.sort === 'name' ? '▲' : '▼'}
                                </span>
                            </button>
                            <span style={{ padding: '11px 16px' }}>{CONFIG_TEMPLATE_COPY.TAGS}</span>
                            <span style={{ padding: '11px 16px' }}>{CONFIG_TEMPLATE_COPY.STATES}</span>
                            <span style={{ padding: '11px 16px' }}>
                                {CONFIG_TEMPLATE_COPY.LICENCE_TYPE}
                            </span>
                            <span style={{ padding: '11px 16px' }}>{CONFIG_TEMPLATE_COPY.STATUS}</span>
                            <span style={{ padding: '11px 16px', textAlign: 'right' }}>
                                {CONFIG_TEMPLATE_COPY.ACTIONS}
                            </span>
                        </div>
                        {place.templates.map((row) => {
                            const licenceNames = row.licenceTypes.map((item) =>
                                licenceLabel(item, catalogStates)
                            );
                            const busy = list.rowBusy[row._id];
                            const failed = list.rowError[row._id];
                            const pendingLabel =
                                busy === 'status'
                                    ? row.activeStatus === 'Active'
                                        ? CONFIG_TEMPLATE_COPY.TURNING_OFF
                                        : CONFIG_TEMPLATE_COPY.TURNING_ON
                                    : busy === 'duplicate'
                                      ? CONFIG_TEMPLATE_COPY.DUPLICATING
                                      : null;
                            return (
                                <div
                                    key={row._id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => list.openEditor(row._id)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') list.openEditor(row._id);
                                    }}
                                    className="grid items-center cursor-pointer hover:bg-[var(--cpm-purple-tint-3)]"
                                    style={{
                                        gridTemplateColumns: TABLE_COLS,
                                        minWidth: 980,
                                        borderBottom: '1px solid var(--cpm-border-default)',
                                    }}
                                >
                                    <div style={{ padding: '13px 16px', minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                                color: 'var(--cpm-text-primary)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.name}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                color: 'var(--cpm-text-tertiary)',
                                                marginTop: 2,
                                            }}
                                        >
                                            {rowSettingsLine(row.settingsCount)}
                                        </div>
                                    </div>
                                    <div style={{ padding: '13px 16px', minWidth: 0 }}>
                                        <ChipOverflow
                                            items={row.tags}
                                            emptyLabel={CONFIG_TEMPLATE_COPY.NO_TAGS}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            padding: '13px 16px',
                                            minWidth: 0,
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            color: 'var(--cpm-text-primary)',
                                        }}
                                    >
                                        {row.allStates
                                            ? CONFIG_TEMPLATE_COPY.ALL_STATES
                                            : row.states.join(', ') || '—'}
                                    </div>
                                    <div style={{ padding: '13px 16px', minWidth: 0 }}>
                                        <ChipOverflow items={licenceNames} />
                                    </div>
                                    <div style={{ padding: '13px 16px' }}>
                                        <CpmBadge
                                            tone={
                                                row.activeStatus === 'Active'
                                                    ? 'success'
                                                    : 'neutral'
                                            }
                                            dot
                                        >
                                            {row.activeStatus === 'Active'
                                                ? CONFIG_TEMPLATE_COPY.ACTIVE
                                                : CONFIG_TEMPLATE_COPY.INACTIVE}
                                        </CpmBadge>
                                    </div>
                                    <div
                                        className="flex items-center justify-end"
                                        style={{ padding: '10px 16px', gap: 6 }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {pendingLabel ? (
                                            <>
                                                <span
                                                    className="animate-pulse"
                                                    style={{
                                                        display: 'block',
                                                        width: 9,
                                                        height: 9,
                                                        flex: 'none',
                                                        borderRadius: 'var(--cpm-radius-full)',
                                                        background: 'var(--cpm-brand-purple)',
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    {pendingLabel}
                                                </span>
                                            </>
                                        ) : failed ? (
                                            <>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: 'var(--cpm-shortfall-text)',
                                                    }}
                                                >
                                                    {rowStatusFailedLabel(row.activeStatus)}
                                                </span>
                                                <CpmButton
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        void list.toggleStatus(
                                                            row._id,
                                                            row.activeStatus
                                                        )
                                                    }
                                                    style={{ height: 32, paddingLeft: 12, paddingRight: 12 }}
                                                >
                                                    {CONFIG_TEMPLATE_COPY.RETRY_ROW}
                                                </CpmButton>
                                            </>
                                        ) : (
                                            <>
                                                <RowGhostButton
                                                    icon={<DuplicateIcon />}
                                                    onClick={() => list.duplicate(row._id)}
                                                >
                                                    {CONFIG_TEMPLATE_COPY.DUPLICATE}
                                                </RowGhostButton>
                                                <RowGhostButton
                                                    onClick={() =>
                                                        void list.toggleStatus(
                                                            row._id,
                                                            row.activeStatus
                                                        )
                                                    }
                                                >
                                                    {row.activeStatus === 'Active'
                                                        ? CONFIG_TEMPLATE_COPY.TURN_OFF
                                                        : CONFIG_TEMPLATE_COPY.TURN_ON}
                                                </RowGhostButton>
                                                <CpmIconButton
                                                    icon={<PencilIcon />}
                                                    label={CONFIG_TEMPLATE_COPY.OPEN_TEMPLATE}
                                                    onClick={() => list.openEditor(row._id)}
                                                    style={{ width: 34, height: 34 }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ padding: '8px 20px 16px' }}>
                        <InfiniteScrollSentinel
                            hasMore={place.templates.length < place.total}
                            loading={place.loadingMore}
                            error={place.moreError}
                            onLoadMore={list.loadMore}
                            pageSize={100}
                            showEnd={place.templates.length > 0}
                        />
                    </div>
                </>
            ) : null}
        </>
    );
}
